import React, { useState, useEffect } from 'react';
import { formatSimpleCurrency } from '../utils/currencyUtils';
import { Car, AdditionalCharge } from '../types';

interface ReturnCarModalProps {
  car: Car;
  onReturn: (newMileage: number, additionalCharges: AdditionalCharge[]) => void;
  onCancel: () => void;
  isOpen: boolean;
}

export const ReturnCarModal: React.FC<ReturnCarModalProps> = ({
  car,
  onReturn,
  onCancel,
  isOpen
}) => {
  const [newMileage, setNewMileage] = useState(car.mileageKm?.toString() || '');
  const [additionalCharges, setAdditionalCharges] = useState<AdditionalCharge[]>([]);
  const [newChargeDescription, setNewChargeDescription] = useState('');
  const [newChargeAmount, setNewChargeAmount] = useState('');
  const [newChargeType, setNewChargeType] = useState<AdditionalCharge['type']>('other');

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setNewMileage(car.mileageKm?.toString() || '');
      setAdditionalCharges([]);
      setNewChargeDescription('');
      setNewChargeAmount('');
      setNewChargeType('other');
    }
  }, [isOpen, car.mileageKm]);

  const chargeTypes: { value: AdditionalCharge['type']; label: string; icon: string }[] = [
    { value: 'cleaning', label: 'Cleaning', icon: '🧽' },
    { value: 'repair', label: 'Repair', icon: '🔧' },
    { value: 'fuel', label: 'Fuel', icon: '⛽' },
    { value: 'damage', label: 'Damage', icon: '💥' },
    { value: 'other', label: 'Other', icon: '📝' }
  ];

  const addCharge = () => {
    if (newChargeDescription.trim() && newChargeAmount) {
      const charge: AdditionalCharge = {
        id: Date.now().toString(),
        description: newChargeDescription.trim(),
        amount: parseFloat(newChargeAmount),
        type: newChargeType
      };
      
      setAdditionalCharges([...additionalCharges, charge]);
      
      setNewChargeDescription('');
      setNewChargeAmount('');
      setNewChargeType('other');
    }
  };

  const removeCharge = (chargeId: string) => {
    setAdditionalCharges(additionalCharges.filter(c => c.id !== chargeId));
  };

  const handleSubmit = () => {
    const mileage = parseInt(newMileage);
    if (mileage && mileage >= (car.mileageKm || 0)) {
      onReturn(mileage, additionalCharges);
    }
  };

  const totalAdditionalCharges = additionalCharges.reduce((sum, charge) => sum + charge.amount, 0);
  const mileageDifference = parseInt(newMileage) - (car.mileageKm || 0);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-orange-500 to-orange-600 px-6 py-4 text-white">
          <h2 className="text-xl font-semibold">Return Car</h2>
          <p className="text-orange-100 text-sm mt-1">
            Update mileage and add any additional charges
          </p>
        </div>

        <div className="p-6 space-y-6">
          {/* Car Info */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="font-medium text-gray-900 mb-2">Car Details</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-500">Plate:</span>
                <span className="ml-2 font-medium">{car.plate}</span>
              </div>
              <div>
                <span className="text-gray-500">Current Mileage:</span>
                <span className="ml-2 font-medium">{car.mileageKm?.toLocaleString()} km</span>
              </div>
              <div>
                <span className="text-gray-500">Rental Start:</span>
                <span className="ml-2 font-medium">
                  {car.rentalStartMileage?.toLocaleString()} km
                </span>
              </div>
              <div>
                <span className="text-gray-500">Rented Since:</span>
                <span className="ml-2 font-medium">
                  {car.rentedFrom ? new Date(car.rentedFrom).toLocaleDateString() : 'N/A'}
                </span>
              </div>
            </div>
          </div>

          {/* New Mileage Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              New Mileage (km) *
            </label>
            <input
              type="number"
              value={newMileage}
              onChange={(e) => setNewMileage(e.target.value)}
              min={car.mileageKm || 0}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              placeholder="Enter current mileage"
            />
            {mileageDifference > 0 && (
              <p className="text-sm text-green-600 mt-1">
                +{mileageDifference.toLocaleString()} km driven during rental
              </p>
            )}
            {parseInt(newMileage) < (car.mileageKm || 0) && (
              <p className="text-sm text-red-600 mt-1">
                New mileage cannot be less than current mileage
              </p>
            )}
          </div>

          {/* Additional Charges Section */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Additional Charges</h3>
            
            {/* Add New Charge */}
            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <div className="md:col-span-2">
                  <input
                    type="text"
                    value={newChargeDescription}
                    onChange={(e) => setNewChargeDescription(e.target.value)}
                    placeholder="Description (e.g., Interior cleaning)"
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                  />
                </div>
                <div>
                  <input
                    type="number"
                    value={newChargeAmount}
                    onChange={(e) => setNewChargeAmount(e.target.value)}
                    placeholder="Amount"
                    step="0.01"
                    min="0"
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                  />
                </div>
                <div>
                  <select
                    value={newChargeType}
                    onChange={(e) => setNewChargeType(e.target.value as AdditionalCharge['type'])}
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                  >
                    {chargeTypes.map(type => (
                      <option key={type.value} value={type.value}>
                        {type.icon} {type.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <button
                onClick={addCharge}
                disabled={!newChargeDescription.trim() || !newChargeAmount}
                className="mt-3 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors text-sm"
              >
                Add Charge
              </button>
            </div>

            {/* Charges List */}
            {additionalCharges.length > 0 && (
              <div className="space-y-2">
                {additionalCharges.map(charge => {
                  const chargeType = chargeTypes.find(t => t.value === charge.type);
                  return (
                    <div key={charge.id} className="flex items-center justify-between bg-white border rounded-lg p-3">
                      <div className="flex items-center space-x-3">
                        <span className="text-lg">{chargeType?.icon}</span>
                        <div>
                          <p className="font-medium text-gray-900">{charge.description}</p>
                          <p className="text-sm text-gray-500">{chargeType?.label}</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        <span className="font-semibold text-gray-900">
                          {formatSimpleCurrency(charge.amount, true)}
                        </span>
                        <button
                          onClick={() => removeCharge(charge.id)}
                          className="text-red-500 hover:text-red-700 transition-colors"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Total */}
            {totalAdditionalCharges > 0 && (
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mt-4">
                <div className="flex justify-between items-center">
                  <span className="font-medium text-orange-900">Total Additional Charges:</span>
                  <span className="text-xl font-bold text-orange-900">
                    {formatSimpleCurrency(totalAdditionalCharges, true)}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center px-6 py-4 bg-gray-50 border-t rounded-b-2xl">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
          >
            Cancel
          </button>
          
          <button
            onClick={handleSubmit}
            disabled={!newMileage || parseInt(newMileage) < (car.mileageKm || 0)}
            className="px-6 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            Complete Return
          </button>
        </div>
      </div>
    </div>
  );
};
